import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Snackbar, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { Link as RouterLink } from 'react-router-dom';
import BotAvatar from './BotAvatar';
import MessageBubble from './MessageBubble';
import StateBoundary from './StateBoundary';
import { useChatViewModel } from '../viewmodels/ChatViewModel';
import { useApprovalActionsViewModel } from '../viewmodels/ApprovalActionsViewModel';
import { useSession } from '../viewmodels/SessionViewModel';

function dayLabel(value) {
  const d = new Date(value);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function Typing({ label }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 1, ml: 6 }}>
      <Box sx={{ display: 'flex', gap: 0.5 }} aria-hidden>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 7, height: 7, borderRadius: '50%', bgcolor: 'text.disabled', animation: 'orkaDot 1.2s infinite ease-in-out', animationDelay: `${i * 0.2}s`,
              '@keyframes orkaDot': { '0%, 80%, 100%': { opacity: 0.3 }, '40%': { opacity: 1 } },
            }}
          />
        ))}
      </Box>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Stack>
  );
}

function Banner({ status, conversation }) {
  if (!status) return null;
  const items = [];
  if (!status.ai) items.push({ key: 'ai', severity: 'warning', text: 'No AI key is connected, so your team cannot answer yet.' });
  else if (!status.store && !status.whatsapp && !status.analytics && conversation && conversation.type === 'group') {
    items.push({ key: 'data', severity: 'info', text: 'Connect WhatsApp, analytics or your store so the team has something to work with.' });
  }
  if (!items.length) return null;
  return (
    <Box sx={{ px: 2, pt: 1 }}>
      {items.map((i) => (
        <Alert key={i.key} severity={i.severity} sx={{ mb: 1 }} action={<Button component={RouterLink} to="/connections" color="inherit" size="small">Connections</Button>}>
          {i.text}
        </Alert>
      ))}
    </Box>
  );
}

/** The open conversation: header, messages, working indicator and the composer. */
export default function ChatPane({
  conversationId, status, onBack, onActivity,
}) {
  const { isOwner } = useSession();
  const vm = useChatViewModel(conversationId, { onActivity });
  const actions = useApprovalActionsViewModel(vm.refresh);
  const [text, setText] = useState('');
  const scroller = useRef(null);
  const stickToBottom = useRef(true);
  const conv = vm.conversation;

  useEffect(() => { setText(''); stickToBottom.current = true; }, [conversationId]);

  useEffect(() => {
    const el = scroller.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [vm.messages, vm.activeRun]);

  const onScroll = () => {
    const el = scroller.current;
    if (el) stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // The last bot text message of each run is where "what it did" is attached.
  const lastOfRun = useMemo(() => {
    const map = new Map();
    vm.messages.forEach((m, i) => { if (m.run_id && m.sender?.type === 'bot' && m.kind === 'text') map.set(m.run_id, i); });
    return new Set(map.values());
  }, [vm.messages]);

  const submit = async () => {
    if (!text.trim() || vm.sending || vm.activeRun) return;
    stickToBottom.current = true;
    const body = text;
    setText('');
    const ok = await vm.send(body);
    if (!ok) setText(body);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  const canPost = conv ? conv.can_post : false;
  const working = Boolean(vm.activeRun);
  const workingLabel = conv?.type === 'direct' && conv.bot ? `${conv.bot.name} is working...` : 'Your team is working...';
  const subtitle = conv ? (conv.type === 'group' ? `${conv.members.length} team members` : conv.bot?.role) : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: 'background.paper' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        {onBack && <IconButton aria-label="Back to chats" onClick={onBack} edge="start"><ArrowBackIcon /></IconButton>}
        {conv && <BotAvatar bot={conv.bot} group={conv.type === 'group'} size={40} />}
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography fontWeight={600} noWrap>{conv?.title || ' '}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>{subtitle}</Typography>
        </Box>
        {working && <Chip size="small" color="primary" variant="outlined" label="Working" />}
      </Stack>

      <Banner status={status} conversation={conv} />

      <Box ref={scroller} onScroll={onScroll} sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 1.5, minHeight: 0 }}>
        <StateBoundary loading={vm.loading} error={vm.error} rows={5}>
          {vm.messages.length === 0 && (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 6 }}>
              <Typography fontWeight={600} color="text.primary">{`Say hello to ${conv?.title || 'your team'}`}</Typography>
              <Typography variant="body2">{conv?.type === 'group' ? 'Messages here go to the whole team.' : 'Ask for what you need in plain words.'}</Typography>
            </Box>
          )}
          {vm.messages.map((m, i) => {
            const prev = vm.messages[i - 1];
            const sameSender = prev && prev.kind !== 'status' && prev.sender?.type === m.sender?.type && prev.sender?.id === m.sender?.id;
            const newDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
            const mine = m.sender?.type === 'human';
            return (
              <React.Fragment key={m.id}>
                {newDay && <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ my: 1.5 }}>{dayLabel(m.created_at)}</Typography>}
                <MessageBubble
                  message={m}
                  isMine={mine}
                  isOwner={isOwner}
                  showHeader={!sameSender || newDay || m.kind === 'approval_card'}
                  approvalActions={actions}
                  showSteps={lastOfRun.has(i)}
                />
              </React.Fragment>
            );
          })}
          {working && <Typing label={workingLabel} />}
        </StateBoundary>
      </Box>

      <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
        {working && isOwner && (
          <Stack direction="row" justifyContent="center" sx={{ mb: 1 }}>
            <Button size="small" color="inherit" startIcon={<StopCircleOutlinedIcon />} onClick={vm.stop}>Stop</Button>
          </Stack>
        )}
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={canPost ? `Message ${conv?.title || ''}` : 'Only the account owner can send messages'}
            disabled={!canPost || vm.loading}
            multiline
            maxRows={6}
            fullWidth
            size="small"
            inputProps={{ 'aria-label': 'Message', maxLength: 2000 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 5 } }}
          />
          <Tooltip title={working ? 'Wait for the team to finish' : 'Send'}>
            <span>
              <IconButton color="primary" aria-label="Send" onClick={submit} disabled={!canPost || !text.trim() || vm.sending || working}>
                {vm.sending ? <CircularProgress size={22} /> : <SendIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Snackbar open={Boolean(vm.sendError || actions.feedback)} autoHideDuration={6000} onClose={() => { vm.clearSendError(); actions.clearFeedback(); }} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {(vm.sendError || actions.feedback) ? (
          <Alert severity={vm.sendError ? 'error' : actions.feedback.severity} onClose={() => { vm.clearSendError(); actions.clearFeedback(); }}>
            {vm.sendError || actions.feedback.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
